import Cookies from "js-cookie"
import * as divolte from "~/src/js/divolte-api.js"
import {
  SITE_SEARCH_TRACKING_EVENT,
  type SiteSearchTrackingDetail,
} from "../../assistance-ui/site-search/tracking"
import {
  affiliateCodeFromSession,
  affiliateCodeFromUrl,
  affiliateReferralFromCookie,
} from "./affiliate-referral.js"
import {
  drivenowEnvironment,
  drivenowOxideVersion,
} from "./env.js"
import {
  siteSearchEntryPoint,
  siteSearchSourcePage,
} from "./site-search-context.js"

export const SITE_SEARCH_DIVOLTE_EVENT = "siteSearchPerformed"
export const SITE_SEARCH_RESULT_CLICKED_DIVOLTE_EVENT =
  "siteSearchResultClicked"
export const SITE_SEARCH_CITATION_CLICKED_DIVOLTE_EVENT =
  "siteSearchCitationClicked"
export const SITE_SEARCH_MORE_RESULTS_DIVOLTE_EVENT =
  "siteSearchMoreResults"

let installed = false

/**
 * Translates the React-owned site-search lifecycle into the established
 * Divolte events. The React surface emits completed, typed facts rather than
 * exposing its internal request lifecycle through HTMX DOM events.
 */
export function installSiteSearchTracking(): void {
  if (installed) {
    return
  }
  installed = true
  window.addEventListener(SITE_SEARCH_TRACKING_EVENT, (event) => {
    if (!(event instanceof CustomEvent)) {
      return
    }
    signalTrackingEvent(
      (event as CustomEvent<SiteSearchTrackingDetail>).detail,
    )
  })
}

function signalTrackingEvent(detail: SiteSearchTrackingDetail): void {
  const context = {
    ...standardPageContext(),
    pageNavigationTrigger: "site_search",
  }
  const entry = entryProperties()
  if (detail.kind === "search_completed") {
    signal(SITE_SEARCH_DIVOLTE_EVENT, {
      ...context,
      properties: {
        ...entry,
        searchDurationMs: detail.durationMs,
        searchErrorMessage: detail.errorMessage,
        searchEventId: detail.eventId,
        searchLimit: detail.limit,
        searchQuery: detail.query,
        searchResultSetId: detail.resultSetId,
        searchResultsCount: detail.resultsCount,
        searchStatus: detail.status,
        searchTrigger: detail.trigger,
      },
    })
    return
  }
  if (detail.kind === "more_completed") {
    signal(SITE_SEARCH_MORE_RESULTS_DIVOLTE_EVENT, {
      ...context,
      properties: {
        ...entry,
        searchDurationMs: detail.durationMs,
        searchErrorMessage: detail.errorMessage,
        searchEventId: detail.eventId,
        searchHasMore: detail.hasMore,
        searchQuery: detail.query,
        searchReachedLimit: detail.reachedLimit,
        searchResultSetId: detail.resultSetId,
        searchResultsAfter: detail.resultsAfter,
        searchResultsBefore: detail.resultsBefore,
        searchResultsRequested: detail.resultsRequested,
        searchStatus: detail.status,
      },
    })
    return
  }
  if (detail.kind === "result_clicked") {
    signal(SITE_SEARCH_RESULT_CLICKED_DIVOLTE_EVENT, {
      ...context,
      properties: {
        ...entry,
        searchEventId: detail.eventId,
        searchPassageId: detail.passageId,
        searchQuery: detail.query,
        searchResultChunkId: detail.chunkId,
        searchResultContentType: detail.contentType,
        searchResultLinkType: detail.linkType,
        searchResultRank: detail.rank,
        searchResultSetId: detail.resultSetId,
        searchResultSourceId: detail.sourceId,
        searchResultUrl: detail.url,
      },
    })
    return
  }
  signal(SITE_SEARCH_CITATION_CLICKED_DIVOLTE_EVENT, {
    ...context,
    properties: {
      ...entry,
      searchPassageId: detail.passageId,
      searchResultChunkId: detail.chunkId,
      searchResultSetId: detail.resultSetId,
      searchResultSourceId: detail.sourceId,
      searchResultUrl: detail.url,
    },
  })
}

/** Identifies the invitation and page that opened this search page. */
function entryProperties(): Record<string, string | undefined> {
  const sourcePage = siteSearchSourcePage()
  return {
    searchEntryPoint: siteSearchEntryPoint(),
    searchSourcePage: sourcePage === "/site-search" ? undefined : sourcePage,
  }
}

function signal(
  eventName: string,
  payload: Record<string, unknown>,
): void {
  try {
    divolte.signal(eventName, payload).catch((error) => {
      captureSignalError(error, eventName)
    })
  } catch (error) {
    captureSignalError(error, eventName)
  }
}

function captureSignalError(error: unknown, eventName: string): void {
  console.warn(`Failed to send ${eventName} signal`, error)
  window.MicroSentry?.captureException?.(error)
}

function standardPageContext(): Record<string, unknown> {
  const affiliateReferral = affiliateCodeFromUrl()
    || affiliateReferralFromCookie()
    || safeAffiliateCodeFromSession()
    || "drivenow"
  const urlParams = new URLSearchParams(window.location.search)
  return {
    affiliateCode: affiliateReferral,
    affiliateReferral,
    countryCode: urlParams.get("countryCode")
      || urlParams.get("cc")
      || undefined,
    environment: drivenowEnvironment(),
    frontendName: "oxide",
    frontendVersion: drivenowOxideVersion(),
    isAdmin: Cookies.get("isAdmin") === "true",
    isTester: Cookies.get("isTester") === "true",
    locationCountryCode: urlParams.get("locationCountryCode")
      || urlParams.get("lcc")
      || undefined,
    pageTitle: document.title,
    pageType: "site_search",
    site: "drivenow",
    username: Cookies.get("username"),
    vehicleCategory: urlParams.get("vehicleCategory")
      || urlParams.get("vc")
      || undefined,
  }
}

function safeAffiliateCodeFromSession(): string | null {
  try {
    return affiliateCodeFromSession()
  } catch {
    return null
  }
}
